import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Card, Field, Input, Textarea, Select, PageHeader, PrimaryButton, SecondaryButton, DangerButton } from "@/components/admin/AdminUI";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import { SlugConflictWarning } from "@/components/admin/SlugConflictWarning";
import { SeoGenerateButton } from "@/components/admin/SeoGenerateButton";
import { RecrawlDiffModal } from "@/components/admin/RecrawlDiffModal";
import { EditorImagesField } from "@/components/admin/EditorImagesField";
import { slugify } from "@/lib/slugify";
import { X, ExternalLink } from "lucide-react";

const EMPTY = {
  brand_id: "", clothing_item_type_id: "", name: "", product_url: "", image_url: "",
  image_gallery: [], editor_images: [], price: "", currency: "EUR", product_description: "",
  crawled_material_text: "",
  materials: [], sustainability_status: "pending_review", public_visibility: false,
  is_featured: false,
  label_verified: false, label_verification_notes: "",
  false_advertising_suspected: false, false_advertising_confirmed: false,
  manually_reviewed: false,
  override_reason: "",
};

export default function AdminClothingEdit() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [recrawling, setRecrawling] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffs, setDiffs] = useState([]);
  const [diffTitle, setDiffTitle] = useState("Recrawl results — review changes");
  const [generatingDisclaimer, setGeneratingDisclaimer] = useState(false);
  const [matToAdd, setMatToAdd] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");

  const { data: item } = useQuery({ queryKey: ["admin-item", id], queryFn: async () => (await api.get(`/admin/clothing-items/${id}`)).data, enabled: !!id });
  const { data: brands = [] } = useQuery({ queryKey: ["admin-brands"], queryFn: async () => (await api.get("/admin/brands")).data });
  const { data: types = [] } = useQuery({ queryKey: ["admin-types"], queryFn: async () => (await api.get("/admin/clothing-types")).data });
  const { data: materials = [] } = useQuery({ queryKey: ["admin-materials"], queryFn: async () => (await api.get("/admin/materials")).data });
  const { data: allCerts = [] } = useQuery({ queryKey: ["admin-certifications"], queryFn: async () => (await api.get("/admin/materials-certifications")).data });

  useEffect(() => { if (item) setForm({ ...EMPTY, ...item, price: item.price ?? "", image_gallery: item.image_gallery || [], editor_images: item.editor_images || [], materials: item.materials || [] }); }, [item]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : (e.target ? e.target.value : e) }));

  function addMaterial() {
    if (!matToAdd) return;
    const m = materials.find((x) => x.id === matToAdd);
    if (!m) return;
    if (form.materials.some((x) => x.material_id === m.id)) return;
    setForm((f) => ({ ...f, materials: [...f.materials, { material_id: m.id, material_name: m.name, material_classification: m.classification, percentage: null, source: "manual" }] }));
    setMatToAdd("");
  }
  function setPct(mid, pct) {
    setForm((f) => ({ ...f, materials: f.materials.map((x) => x.material_id === mid ? { ...x, percentage: pct === "" ? null : parseFloat(pct) } : x) }));
  }
  function removeMat(mid) {
    setForm((f) => ({ ...f, materials: f.materials.filter((x) => x.material_id !== mid) }));
  }
  function updateMat(mid, patch) {
    setForm((f) => ({
      ...f,
      materials: f.materials.map((x) => x.material_id === mid ? { ...x, ...patch } : x),
    }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form, price: form.price === "" ? null : parseFloat(form.price) };
      if (isNew) {
        const res = await api.post("/admin/clothing-items", payload);
        toast.success("Item created");
        navigate(`/admin/clothing-items/${res.data.id}`);
      } else {
        await api.patch(`/admin/clothing-items/${id}`, payload);
        toast.success("Saved");
        qc.invalidateQueries({ queryKey: ["admin-item", id] });
        qc.invalidateQueries({ queryKey: ["admin-clothing"] });
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det && typeof det === "object" && det.error === "slug_conflict") {
        toast.error(`URL "/clothing/${form.slug || slugify(form.name)}" is already taken by another clothing item${det.name ? ` ("${det.name}")` : ""}. Change the name.`);
      } else {
        toast.error(typeof det === "string" ? det : "Save failed");
      }
    }
    finally { setSaving(false); }
  }

  async function recrawl(usePaid = false) {
    if (!form.product_url) {
      toast.error("Add a Product URL first");
      return;
    }
    setRecrawling(true);
    try {
      const res = await api.post("/admin/clothing-items/recrawl-url", {
        product_url: form.product_url,
        brand_id: form.brand_id || null,
        clothing_item_id: id || null,
        use_scraperapi: !!usePaid,
      });
      // Invalidate the admin list so the "Link health" column reflects the
      // successful recrawl (backend sets link_status="ok" on success). Also
      // refresh the single-item cache so the form state picks up the new
      // link_status — otherwise a subsequent Save would overwrite the fresh
      // "ok" with whatever stale link_status the form was carrying.
      qc.invalidateQueries({ queryKey: ["admin-clothing"] });
      qc.invalidateQueries({ queryKey: ["admin-item", id] });
      const { image_url, price, currency, product_description } = res.data || {};
      const FIELDS = [
        { key: "image_url", label: "Image URL", oldValue: form.image_url || "", newValue: image_url ?? null },
        { key: "price", label: "Price", oldValue: form.price === "" ? null : Number(form.price), newValue: price ?? null },
        { key: "currency", label: "Currency", oldValue: form.currency || "", newValue: currency || "" },
        { key: "product_description", label: "Product description", oldValue: form.product_description || "", newValue: product_description ?? "" },
      ];
      const changed = FIELDS.filter((f) => {
        if (f.newValue === null || f.newValue === undefined) return false;
        if (f.key === "price") return Number(f.oldValue) !== Number(f.newValue);
        return String(f.oldValue || "") !== String(f.newValue || "");
      });
      if (changed.length === 0) {
        toast.success("Recrawled — no changes detected");
      } else {
        setDiffTitle("Recrawl results — review changes");
        setDiffs(changed);
        setDiffOpen(true);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Recrawl failed");
    } finally {
      setRecrawling(false);
    }
  }

  function applyDiffField(key, value) {
    setForm((f) => {
      const next = { ...f };
      if (key === "price") next.price = value == null ? "" : value;
      else next[key] = value ?? "";
      return next;
    });
    toast.success("Applied — click Save to persist");
  }

  async function generateDisclaimer() {
    if (!id) {
      toast.error("Save the item first before generating a disclaimer");
      return;
    }
    setGeneratingDisclaimer(true);
    const t = toast.loading("Generating disclaimer with AI…");
    try {
      const res = await api.post(`/admin/clothing-items/${id}/generate-disclaimer`);
      const newText = (res.data?.disclaimer || "").trim();
      if (!newText) {
        toast.error("LLM returned an empty disclaimer", { id: t });
        return;
      }
      toast.dismiss(t);
      setDiffTitle("Generated editorial disclaimer — approve to use it");
      setDiffs([{
        key: "override_reason",
        label: "Editorial disclaimer",
        oldValue: form.override_reason || "",
        newValue: newText,
      }]);
      setDiffOpen(true);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Disclaimer generation failed", { id: t });
    } finally {
      setGeneratingDisclaimer(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/clothing-items/${id}`);
    toast.success("Deleted");
    navigate("/admin/clothing-items");
  }

  function addGallery() {
    if (!galleryUrl) return;
    setForm((f) => ({ ...f, image_gallery: [...(f.image_gallery || []), galleryUrl] }));
    setGalleryUrl("");
  }
  function removeGallery(idx) {
    setForm((f) => ({ ...f, image_gallery: f.image_gallery.filter((_, i) => i !== idx) }));
  }

  const effectiveClass = (m) => m.override_classification || m.material_classification;
  const nonGreen = form.materials.filter((m) => effectiveClass(m) !== "GREEN");

  return (
    <div data-testid="admin-clothing-edit">
      <RecrawlDiffModal
        open={diffOpen}
        onOpenChange={setDiffOpen}
        diffs={diffs}
        title={diffTitle}
        onApproveField={applyDiffField}
      />
      <PageHeader title={isNew ? "New clothing item" : `Review: ${form.name || "..."}`}
        actions={<>
          {!isNew && (
            <>
              <SecondaryButton
                onClick={() => recrawl(false)}
                disabled={recrawling || !form.product_url}
                data-testid="item-recrawl-button"
                title={!form.product_url ? "Add a Product URL first" : "Free crawler — re-extract image, price, currency and description"}
              >
                {recrawling ? "Recrawling…" : "Recrawl"}
              </SecondaryButton>
              <SecondaryButton
                onClick={() => recrawl(true)}
                disabled={recrawling || !form.product_url}
                data-testid="item-recrawl-paid-button"
                title={!form.product_url
                  ? "Add a Product URL first"
                  : "Paid crawler via ScraperAPI (~10 credits/fetch; escalates to ultra-premium if needed). Use when the free crawler gets blocked."}
              >
                {recrawling ? "…" : "$ Paid"}
              </SecondaryButton>
            </>
          )}
          <PrimaryButton onClick={save} disabled={saving} data-testid="item-save-button">{saving ? "Saving…" : "Save"}</PrimaryButton>
          {!isNew && <DangerButton onClick={remove}>Delete</DangerButton>}
        </>}
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Section 1 — Brand-sourced product info */}
          <Card className="space-y-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6C757D] -mb-1">1. From the brand</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Brand">
                <Select value={form.brand_id} onChange={set("brand_id")} data-testid="item-brand-select">
                  <option value="">Select brand…</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Select>
              </Field>
              <Field label="Clothing type">
                <Select value={form.clothing_item_type_id || ""} onChange={set("clothing_item_type_id")}>
                  <option value="">—</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </Field>
            </div>
            <Field label="Product name"><Input value={form.name} onChange={set("name")} data-testid="item-name-input" /></Field>
            <Field label="Product URL">
              <div className="flex items-stretch gap-2">
                <Input value={form.product_url || ""} onChange={set("product_url")} data-testid="item-product-url-input" />
                <a
                  href={form.product_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-disabled={!form.product_url}
                  onClick={(e) => { if (!form.product_url) e.preventDefault(); }}
                  className={`inline-flex items-center gap-1.5 px-3 rounded-md text-sm whitespace-nowrap border ${form.product_url ? "border-[#E9ECEF] bg-white hover:border-forest hover:text-forest" : "border-[#E9ECEF] bg-[#F8F9FA] text-[#ADB5BD] cursor-not-allowed"}`}
                  title={form.product_url ? "Open this product page on the brand's website (new tab)" : "Paste a Product URL first"}
                  data-testid="open-product-url-button"
                >
                  <ExternalLink className="w-4 h-4" /> Open on brand site
                </a>
              </div>
            </Field>
            <Field label="Primary image URL (from brand site — hot-linked)"><Input value={form.image_url || ""} onChange={set("image_url")} /></Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Price"><Input type="number" step="0.01" value={form.price ?? ""} onChange={set("price")} /></Field>
              <Field label="Currency"><Input value={form.currency || ""} onChange={set("currency")} /></Field>
              <Field label="Status">
                <Select value={form.sustainability_status} onChange={set("sustainability_status")} data-testid="item-status-select">
                  <option value="pending_review">Pending review</option>
                  <option value="approved_featured">Approved (visible on website)</option>
                  <option value="not_sustainable">Not sustainable</option>
                  <option value="needs_more_information">Needs more information</option>
                  <option value="archived">Archived</option>
                </Select>
              </Field>
            </div>
            <Field label="Description"><Textarea rows={3} value={form.product_description || ""} onChange={set("product_description")} /></Field>
            <Field label="Crawled material text"><Textarea rows={2} value={form.crawled_material_text || ""} onChange={set("crawled_material_text")} /></Field>
          </Card>

          {/* Section 2 — Materials composition */}
          <Card className="space-y-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6C757D] -mb-1">2. Material composition</div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">Materials attached</div>
              <div className="space-y-2">
                {form.materials.map((m) => {
                  const selectedCerts = (m.certification_ids || []).map((cid) => allCerts.find((c) => c.id === cid)).filter(Boolean);
                  const eff = m.override_classification || m.material_classification;
                  return (
                    <div key={m.material_id} className="border border-[#E9ECEF] rounded-md px-3 py-2 space-y-2">
                      <div className="flex items-center gap-2">
                        <ClassificationBadge classification={m.material_classification} className="text-[10px]" />
                        <span className="flex-1 text-sm font-medium">{m.material_name}</span>
                        <input type="number" placeholder="%" className="w-20 h-8 px-2 border border-[#E9ECEF] rounded text-sm" value={m.percentage ?? ""} onChange={(e) => setPct(m.material_id, e.target.value)} />
                        <button onClick={() => removeMat(m.material_id)} className="text-[#6C757D]"><X className="w-4 h-4" /></button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 pl-1">
                        <span className="text-[11px] text-[#6C757D]">Certifications:</span>
                        {selectedCerts.map((c) => (
                          <span key={c.id} className="inline-flex items-center gap-1 bg-leaf/10 border border-leaf/30 text-xs px-2 py-0.5 rounded-full">
                            {c.name}
                            <button
                              type="button"
                              onClick={() => {
                                const nextCerts = (m.certification_ids || []).filter((x) => x !== c.id);
                                const patch = { certification_ids: nextCerts };
                                if (nextCerts.length === 0) patch.override_classification = null;
                                updateMat(m.material_id, patch);
                              }}
                              className="text-[#6C757D]"
                            >×</button>
                          </span>
                        ))}
                        <Select
                          value=""
                          onChange={(e) => {
                            const cid = e.target.value;
                            if (!cid) return;
                            updateMat(m.material_id, { certification_ids: [...(m.certification_ids || []), cid] });
                          }}
                          className="h-7 text-xs"
                          data-testid={`add-cert-${m.material_id}`}
                        >
                          <option value="">+ Add certification…</option>
                          {allCerts.filter((c) => !(m.certification_ids || []).includes(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </Select>
                        {(m.certification_ids || []).length > 0 && (
                          <>
                            <span className="text-[11px] text-[#6C757D] ml-2">Reclassify to:</span>
                            <Select
                              value={m.override_classification || ""}
                              onChange={(e) => updateMat(m.material_id, { override_classification: e.target.value || null })}
                              className="h-7 text-xs"
                              data-testid={`override-${m.material_id}`}
                            >
                              <option value="">— no override —</option>
                              <option value="GREEN">GREEN</option>
                              <option value="YELLOW">YELLOW</option>
                            </Select>
                            {m.override_classification && m.override_classification !== m.material_classification && (
                              <span className="text-[11px] text-forest">→ effective: <strong>{eff}</strong></span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                <Select value={matToAdd} onChange={(e) => setMatToAdd(e.target.value)} className="flex-1">
                  <option value="">Add material…</option>
                  {materials.filter((x) => !form.materials.some((m) => m.material_id === x.id)).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.classification})</option>)}
                </Select>
                <SecondaryButton onClick={addMaterial} data-testid="add-material-button">Add</SecondaryButton>
              </div>
            </div>
          </Card>

          {/* Section 3 — Editorial review + images */}
          <Card className="space-y-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6C757D] -mb-1">3. Editorial review &amp; images</div>
            {form.sustainability_status === "approved_featured" && (
              <Field
                label={
                  <span className="flex items-center justify-between gap-2 w-full">
                    <span>
                      Editorial review{" "}
                      {nonGreen.length > 0 ? (
                        <span className="font-normal normal-case tracking-normal text-[11px] text-clay">(public — required to approve with non-GREEN materials)</span>
                      ) : (
                        <span className="font-normal normal-case tracking-normal text-[11px] text-[#6C757D]">(public — optional for all-GREEN items)</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={generateDisclaimer}
                      disabled={generatingDisclaimer || !id}
                      title={!id ? "Save the item first" : "Generate an editorial review with AI based on materials and brand claims"}
                      className="inline-flex items-center gap-1 text-xs font-medium text-leaf hover:underline disabled:opacity-50 disabled:no-underline normal-case tracking-normal"
                      data-testid="generate-disclaimer-button"
                    >
                      {generatingDisclaimer ? "Generating…" : "✨ Generate review"}
                    </button>
                  </span>
                }
                hint={nonGreen.length > 0
                  ? "Shown on the public product page in a highlighted callout. Required when approving an item with YELLOW or RED materials — explain why it was approved."
                  : "Optional editorial note shown on the public product page. Add context like sourcing, durability, or styling that goes beyond the brand's own description."}
              >
                <Textarea rows={3} value={form.override_reason || ""} onChange={set("override_reason")} data-testid="item-override-reason" />
              </Field>
            )}

            {/* Image gallery (Cloudinary uploads) */}
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">Image gallery (your uploads to Cloudinary)</div>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {form.image_gallery.map((u, i) => (
                  <div key={`${u}-${i}`} className="relative">
                    <img src={u} alt="" className="aspect-square object-cover rounded" />
                    <button onClick={() => removeGallery(i)} className="absolute top-1 right-1 bg-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <CloudinaryUploader
                  mode="admin"
                  folder={`sustainably_curated/items/${id || "new"}`}
                  multiple={true}
                  label="Upload gallery images"
                  onUploaded={(url) => setForm((f) => ({ ...f, image_gallery: [...(f.image_gallery || []), url] }))}
                />
                <span className="text-xs text-[#6C757D]">— or paste a URL:</span>
                <Input value={galleryUrl} onChange={(e) => setGalleryUrl(e.target.value)} placeholder="https://…" className="flex-1 min-w-[200px]" />
                <SecondaryButton onClick={addGallery}>Add URL</SecondaryButton>
              </div>
            </div>

            {/* Editor's images — shown publicly on the clothing detail page */}
            <EditorImagesField
              value={form.editor_images}
              onChange={(next) => setForm((f) => ({ ...f, editor_images: next }))}
            />
          </Card>
        </div>

        <Card className="space-y-4 h-fit">
          <div className="flex items-center justify-between"><span className="text-sm">Public visibility</span><input type="checkbox" checked={!!form.public_visibility} onChange={set("public_visibility")} /></div>

          <label className="flex items-start gap-2 text-sm cursor-pointer border-t border-[#E9ECEF] pt-4">
            <input
              type="checkbox"
              checked={!!form.manually_reviewed}
              onChange={set("manually_reviewed")}
              className="accent-forest mt-0.5"
              data-testid="item-manually-reviewed-toggle"
            />
            <span>
              Manually reviewed
              <span className="block text-xs text-[#6C757D] mt-0.5">
                Admin-only — flag items you've personally double-checked on the brand's site. Never exposed publicly.
              </span>
            </span>
          </label>

          {form.sustainability_status === "approved_featured" && (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_featured}
                onChange={set("is_featured")}
                className="accent-forest mt-0.5"
                data-testid="item-featured-toggle"
              />
              <span>Featured (highlight this item with a green pill on cards &amp; detail page)</span>
            </label>
          )}

          <Field
            label="Editorial materials flag"
            hint="Choose one. Shown as a pill on the product card and a callout on the detail page."
          >
            <Select
              data-testid="item-materials-flag-select"
              value={
                form.false_advertising_confirmed
                  ? "materials_mislabeled"
                  : form.false_advertising_suspected
                    ? "materials_disputed"
                    : form.label_verified
                      ? "label_verified"
                      : "none"
              }
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({
                  ...f,
                  label_verified: v === "label_verified",
                  false_advertising_suspected: v === "materials_disputed",
                  false_advertising_confirmed: v === "materials_mislabeled",
                }));
              }}
            >
              <option value="none">None (no pill shown)</option>
              <option value="label_verified">Label verified</option>
              <option value="materials_disputed">Materials disputed (suspected)</option>
              <option value="materials_mislabeled">Materials mislabeled (confirmed)</option>
            </Select>
          </Field>
          {form.label_verified && (
            <Field label="Label verification notes"><Textarea rows={3} value={form.label_verification_notes || ""} onChange={set("label_verification_notes")} /></Field>
          )}
          {form.image_url && <img src={form.image_url} alt="" className="w-full rounded mt-3" />}

          <div className="pt-3 border-t border-[#E9ECEF]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">SEO</h3>
              <SeoGenerateButton
                disabled={!form.name}
                buildPayload={() => {
                  const brand = brands.find((b) => b.id === form.brand_id);
                  const type = types.find((t) => t.id === form.clothing_item_type_id);
                  const matNames = (form.materials || [])
                    .map((m) => {
                      const meta = materials.find((mat) => mat.id === m.material_id);
                      const label = meta?.name || "";
                      return m.percent != null ? `${label} ${m.percent}%` : label;
                    })
                    .filter(Boolean);
                  return {
                    kind: "clothing",
                    name: form.name,
                    brand_name: brand?.name,
                    item_type: type?.name,
                    sustainability_status: form.sustainability_status,
                    materials: matNames,
                    price: form.price ? Number(form.price) : undefined,
                    description: form.product_description,
                  };
                }}
                onResult={({ seo_title, seo_description }) =>
                  setForm((f) => ({ ...f, seo_title, seo_description }))
                }
              />
            </div>
            <Field label="SEO title" hint="Used in browser tab + Google results. Falls back to the item name."><Input value={form.seo_title || ""} onChange={set("seo_title")} maxLength={120} /></Field>
            <Field label="SEO description" hint="155–160 chars ideally. Falls back to the product description."><Textarea rows={2} value={form.seo_description || ""} onChange={set("seo_description")} maxLength={300} /></Field>
            <p className="text-[11px] text-[#6C757D] mt-1">URL: <code>/clothing/{form.slug || slugify(form.name) || "<slug>"}</code> (automatic)</p>
            <SlugConflictWarning slug={form.slug || slugify(form.name)} kind="clothing" ignoreId={id} urlPrefix="/clothing" />
          </div>
        </Card>
      </div>
    </div>
  );
}
